import { useEffect, useState } from 'react';
import { LogOut, UtensilsCrossed, FolderOpen, Package, Settings as SettingsIcon, Eye, UserCog, ShieldCheck, Lock } from 'lucide-react';
import { changeAdminPassword, clearAdminSession, createAdminUser, getStoredAdminUser, listAdminUsers, listTable, resetAdminUserPassword } from '@/lib/api';
import type { Category, Product, Settings } from '@/types';
import { ProductManager } from '@/views/ProductManager';
import { CategoryManager } from '@/views/CategoryManager';
import { SettingsManager } from '@/views/SettingsManager';

type Tab = 'products' | 'categories' | 'settings';

export function AdminDashboard() {
  const [tab, setTab] = useState<Tab>('products');
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    refresh();
  }, []);

  const refresh = async () => {
    const [catRes, prodRes, setRes] = await Promise.all([
      listTable<Category>('categories'),
      listTable<Product>('products'),
      listTable<Settings>('settings').then((rows) => rows[0] ?? null),
    ]);
    setCategories(catRes || []);
    setProducts(prodRes || []);
    setSettings(setRes);
    setLoading(false);
  };

  const handleLogout = async () => {
    clearAdminSession();
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const tabs: { id: Tab; label: string; icon: typeof Package }[] = [
    { id: 'products', label: 'Produtos', icon: Package },
    { id: 'categories', label: 'Categorias', icon: FolderOpen },
    { id: 'settings', label: 'Configurações', icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen bg-warm-50">
      <header className="sticky top-0 z-30 border-b border-warm-200/60 bg-white/90 backdrop-blur-lg">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-500 text-white">
            <UtensilsCrossed size={20} />
          </div>
          <div className="flex-1">
            <h1 className="font-display font-bold text-warm-900">Painel Admin</h1>
            <p className="text-xs text-warm-500">{settings?.business_name || 'Cardápio'}</p>
          </div>
          <button
            onClick={() => {
              window.history.pushState({}, '', '/');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-warm-100 text-warm-600 transition-colors hover:bg-warm-200"
            title="Ver cardápio"
          >
            <Eye size={18} />
          </button>
          <button
            onClick={handleLogout}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-500 transition-colors hover:bg-red-100"
            title="Sair"
          >
            <LogOut size={18} />
          </button>
        </div>

        <div className="mx-auto flex max-w-4xl gap-1 px-4 pb-2">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all ${
                tab === t.id
                  ? 'bg-brand-50 text-brand-700'
                  : 'text-warm-500 hover:bg-warm-100'
              }`}
            >
              <t.icon size={16} />
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-5">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-warm-200 border-t-brand-500" />
          </div>
        ) : tab === 'products' ? (
          <ProductManager categories={categories} products={products} onRefresh={refresh} />
        ) : tab === 'categories' ? (
          <CategoryManager categories={categories} onRefresh={refresh} />
        ) : tab === 'settings' ? (
          <div className="space-y-6">
            <SettingsManager settings={settings} onRefresh={refresh} />
            <AdminAccountPanel />
          </div>
        ) : null}
      </main>
    </div>
  );
}

function AdminAccountPanel() {
  const currentUser = getStoredAdminUser();
  const [users, setUsers] = useState<Array<{ id: number; email: string; name: string; is_active: number; created_at: string }>>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [resetPasswords, setResetPasswords] = useState<Record<number, string>>({});
  const [resettingId, setResettingId] = useState<number | null>(null);
  const [resetSuccess, setResetSuccess] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [creating, setCreating] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoadingUsers(true);
    setUsersError('');
    try {
      const response = await listAdminUsers();
      setUsers(response.data || []);
    } catch (err: unknown) {
      setUsersError(err instanceof Error ? err.message : 'Erro ao carregar usuários.');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleChangePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword.length < 6) {
      setPasswordError('A nova senha deve ter pelo menos 6 caracteres.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('A confirmação da nova senha não bate.');
      return;
    }

    try {
      await changeAdminPassword(currentPassword, newPassword);
      setPasswordSuccess('Senha alterada com sucesso.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      setPasswordError(err instanceof Error ? err.message : 'Erro ao alterar a senha.');
    }
  };

  const handleCreateUser = async (event: React.FormEvent) => {
    event.preventDefault();
    setCreateError('');
    setCreateSuccess('');

    if (!newUserName.trim() || !newUserEmail.trim() || !newUserPassword.trim()) {
      setCreateError('Preencha nome, e-mail e senha do novo acesso.');
      return;
    }

    try {
      setCreating(true);
      await createAdminUser(newUserName.trim(), newUserEmail.trim(), newUserPassword);
      setCreateSuccess('Novo acesso criado com sucesso.');
      setNewUserName('');
      setNewUserEmail('');
      setNewUserPassword('');
      loadUsers();
    } catch (err: unknown) {
      setCreateError(err instanceof Error ? err.message : 'Erro ao criar o acesso.');
    } finally {
      setCreating(false);
    }
  };

  const handleResetUserPassword = async (userId: number) => {
    const nextPassword = (resetPasswords[userId] || '').trim();
    setResetSuccess('');
    setUsersError('');

    if (nextPassword.length < 6) {
      setUsersError('A nova senha do usuário deve ter pelo menos 6 caracteres.');
      return;
    }

    try {
      setResettingId(userId);
      await resetAdminUserPassword(userId, nextPassword);
      setResetPasswords((prev) => ({ ...prev, [userId]: '' }));
      setResetSuccess('Senha atualizada com sucesso.');
    } catch (err: unknown) {
      setUsersError(err instanceof Error ? err.message : 'Erro ao atualizar senha do usuário.');
    } finally {
      setResettingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="card p-5">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
            <ShieldCheck size={20} />
          </div>
          <div>
            <h2 className="font-display text-xl font-bold text-warm-900">Minha conta</h2>
            <p className="text-sm text-warm-500">{currentUser?.email || 'Administrador'}</p>
          </div>
        </div>

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-warm-700">Senha atual</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="input-field"
              placeholder="Sua senha atual"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-warm-700">Nova senha</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="input-field"
              placeholder="Nova senha"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-warm-700">Confirmar nova senha</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input-field"
              placeholder="Repita a nova senha"
            />
          </div>

          {passwordError && <p className="text-sm text-red-600">{passwordError}</p>}
          {passwordSuccess && <p className="text-sm text-green-600">{passwordSuccess}</p>}

          <button type="submit" className="btn-primary flex items-center gap-2">
            <Lock size={18} />
            Alterar senha
          </button>
        </form>
      </div>

      <div className="card p-5">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
            <UserCog size={20} />
          </div>
          <div>
            <h2 className="font-display text-xl font-bold text-warm-900">Criar novo acesso</h2>
            <p className="text-sm text-warm-500">Somente um administrador autenticado pode criar outro usuário.</p>
          </div>
        </div>

        <form onSubmit={handleCreateUser} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-warm-700">Nome</label>
            <input
              type="text"
              value={newUserName}
              onChange={(e) => setNewUserName(e.target.value)}
              className="input-field"
              placeholder="Nome do novo admin"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-warm-700">E-mail</label>
            <input
              type="email"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              className="input-field"
              placeholder="novo@admin.com"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-warm-700">Senha</label>
            <input
              type="password"
              value={newUserPassword}
              onChange={(e) => setNewUserPassword(e.target.value)}
              className="input-field"
              placeholder="Defina uma senha"
            />
          </div>

          {createError && <p className="text-sm text-red-600">{createError}</p>}
          {createSuccess && <p className="text-sm text-green-600">{createSuccess}</p>}

          <button type="submit" disabled={creating} className="btn-primary flex items-center gap-2">
            {creating ? 'Criando...' : 'Criar acesso'}
          </button>
        </form>
      </div>

      <div className="card p-5">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
            <UserCog size={20} />
          </div>
          <div>
            <h2 className="font-display text-xl font-bold text-warm-900">Usuários cadastrados</h2>
            <p className="text-sm text-warm-500">Defina uma nova senha para qualquer usuário admin.</p>
          </div>
        </div>

        {loadingUsers ? <p className="text-sm text-warm-500">Carregando usuários...</p> : null}
        {usersError ? <p className="mb-3 text-sm text-red-600">{usersError}</p> : null}
        {resetSuccess ? <p className="mb-3 text-sm text-green-600">{resetSuccess}</p> : null}

        <div className="space-y-3">
          {users.map((user) => (
            <div key={user.id} className="rounded-xl border border-warm-200 p-3">
              <div className="mb-2">
                <p className="font-medium text-warm-900">{user.name || user.email}</p>
                <p className="text-sm text-warm-500">{user.email}</p>
              </div>

              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <input
                  type="password"
                  className="input-field"
                  value={resetPasswords[user.id] || ''}
                  onChange={(e) =>
                    setResetPasswords((prev) => ({
                      ...prev,
                      [user.id]: e.target.value,
                    }))
                  }
                  placeholder="Nova senha (min. 6)"
                />
                <button
                  type="button"
                  onClick={() => handleResetUserPassword(user.id)}
                  disabled={resettingId === user.id}
                  className="btn-primary"
                >
                  {resettingId === user.id ? 'Salvando...' : 'Atualizar senha'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
